import { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

function History() {
  const [audits, setAudits] = useState([]);
  const [moves, setMoves] = useState([]);
  const [tab, setTab] = useState('audits');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchHistory() {
      setLoading(true);
      try {
        const { data: auditData } = await supabase
          .from('bin_audit_dates')
          .select('*')
          .order('last_audited', { ascending: false });
        if (auditData) setAudits(auditData);

        const { data: moveData } = await supabase
          .from('storage_history')
          .select('*')
          .order('moved_at', { ascending: false })
          .limit(100);
        if (moveData) setMoves(moveData);
      } catch (err) {
        console.error('Error fetching history:', err);
      }
      setLoading(false);
    }
    fetchHistory();
  }, []);

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function formatTime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  const tabs = [
    { key: 'audits', label: 'Bins Audited', count: audits.length, accent: colors.greenDark },
    { key: 'moves', label: 'Storage Moves', count: moves.length, accent: '#2563eb' },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      fontFamily: "'Outfit', sans-serif",
    }}>
      <PageHeader title="History" icon="≡" />

      {/* Tab stat cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '10px',
        marginBottom: '20px',
      }}>
        {tabs.map(t => {
          const isActive = tab === t.key;
          return (
            <div
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                background: isActive ? colors.navy : colors.cardBg,
                border: `1px solid ${isActive ? colors.navy : colors.border}`,
                borderBottom: `3px solid ${t.accent}`,
                borderRadius: '6px',
                padding: '16px',
                textAlign: 'center',
                cursor: 'pointer',
              }}
            >
              <div style={{
                fontSize: '1.8rem',
                fontWeight: 800,
                color: isActive ? '#ffffff' : t.accent,
              }}>{t.count}</div>
              <div style={{
                fontSize: '0.68rem',
                color: isActive ? '#94a3b8' : colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginTop: '4px',
              }}>{t.label}</div>
            </div>
          );
        })}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '40px',
          textAlign: 'center',
          color: colors.textSecondary,
          fontSize: '0.9rem',
        }}>Loading history...</div>
      )}

      {/* Audits tab */}
      {!loading && tab === 'audits' && (
        <>
          {audits.length === 0 && (
            <div style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '40px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '12px', color: colors.navy }}>—</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary }}>
                No audits recorded yet
              </div>
            </div>
          )}
          {audits.map((audit, i) => (
            <div key={i} style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderLeft: `3px solid ${colors.greenDark}`,
              borderRadius: '6px',
              padding: '14px 16px',
              marginBottom: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{
                fontSize: '1rem',
                fontWeight: 800,
                color: colors.textPrimary,
              }}>{audit.bin_name}</div>
              <div style={{
                fontSize: '0.75rem',
                color: colors.greenDark,
                fontWeight: 600,
              }}>✓ {formatDate(audit.last_audited)}</div>
            </div>
          ))}
        </>
      )}

      {/* Moves tab */}
      {!loading && tab === 'moves' && (
        <>
          {moves.length === 0 && (
            <div style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '40px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '12px', color: colors.navy }}>—</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary }}>
                No storage moves recorded yet
              </div>
            </div>
          )}
          {moves.map((move, i) => (
            <div key={i} style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderLeft: '3px solid #2563eb',
              borderRadius: '6px',
              padding: '14px 16px',
              marginBottom: '6px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '6px',
              }}>
                <div style={{
                  fontSize: '0.92rem',
                  fontWeight: 800,
                  color: colors.textPrimary,
                }}>{move.part_no}</div>
                <div style={{
                  fontSize: '0.7rem',
                  color: colors.textSecondary,
                }}>{formatDate(move.moved_at)} · {formatTime(move.moved_at)}</div>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.82rem',
              }}>
                <span style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  color: '#dc2626',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                }}>{move.from_bin}</span>
                <span style={{ color: colors.textSecondary }}>→</span>
                <span style={{
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  color: colors.greenDark,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                }}>{move.to_bin}</span>
                {move.color_name && (
                  <span style={{
                    color: colors.textSecondary,
                    fontSize: '0.72rem',
                  }}>· {move.color_name}</span>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default History;